import { useNavigate } from "react-router-dom";
import { useEffect } from "react";
import { Users, DollarSign, CalendarDays, Newspaper, LogOut, Home, Wallet } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useProfile } from "@/hooks/useProfile";
import { useRecepcionistaRedirect } from "@/hooks/useRecepcionistaRedirect";
import { supabase } from "@/integrations/supabase/client";

const Recepcionista = () => {
  const navigate = useNavigate();
  const { profile } = useProfile();
  const { isPureRecepcionista } = useRecepcionistaRedirect();
  const gymId = profile?.gym_id;

  // A user with a dual role (e.g. a profesor that also has recepcionista
  // access) should be able to return to the rest of the app without losing
  // their session. Pure recepcionistas only get a "Salir" (sign out) button.
  const isDualRoleRecepcionista = isPureRecepcionista === false;

  useEffect(() => {
    document.title = "Panel de Recepcionista | Clubbers";
  }, []);

  const handleSignOut = async () => {
    await supabase.auth.signOut();
    navigate("/intro", { replace: true });
  };

  const cards = [
    {
      title: "Usuarios",
      description: "Gestionar socios y miembros",
      icon: Users,
      onClick: () => navigate("/total-usuarios"),
      gradient: "from-blue-500/20 to-cyan-500/20",
      iconColor: "text-blue-400",
    },
    {
      title: "Pagos e Historial",
      description: "Registrar pagos y revisar comprobantes",
      icon: DollarSign,
      onClick: () => navigate("/total-pagos"),
      gradient: "from-emerald-500/20 to-green-500/20",
      iconColor: "text-emerald-400",
    },
    {
      title: "Reservas",
      description: "Administrar reservas del gimnasio",
      icon: CalendarDays,
      onClick: () => gymId && navigate(`/gestion-reservas/${gymId}`),
      gradient: "from-purple-500/20 to-pink-500/20",
      iconColor: "text-purple-400",
      disabled: !gymId,
    },
    {
      title: "Noticias",
      description: "Crear y editar noticias",
      icon: Newspaper,
      onClick: () => gymId && navigate(`/gestion-noticias/${gymId}`),
      gradient: "from-orange-500/20 to-amber-500/20",
      iconColor: "text-orange-400",
      disabled: !gymId,
    },
    {
      title: "Arqueo de Caja",
      description: "Cierre diario de caja",
      icon: Wallet,
      onClick: () => navigate("/arqueo-caja"),
      gradient: "from-yellow-500/20 to-orange-500/20",
      iconColor: "text-yellow-400",
    },
  ];

  return (
    <div className="min-h-screen bg-gradient-to-b from-primary/10 via-background to-background text-primary pt-16 px-4 sm:px-6 pb-10">
      <div className="max-w-5xl mx-auto">
        <header className="mb-10 flex items-start justify-between gap-4">
          <div>
            <h1 className="text-3xl sm:text-4xl font-bold mb-2">
              Panel de Recepcionista
            </h1>
            <p className="text-muted-foreground">
              {profile?.first_name ? `Hola, ${profile.first_name}. ` : ""}
              Selecciona una sección para continuar.
            </p>
          </div>
          {isDualRoleRecepcionista ? (
            <Button variant="outline" size="sm" onClick={() => navigate("/")}>
              <Home className="h-4 w-4 mr-2" />
              Volver al Home
            </Button>
          ) : (
            <Button variant="outline" size="sm" onClick={handleSignOut}>
              <LogOut className="h-4 w-4 mr-2" />
              Salir
            </Button>
          )}
        </header>

        <div className="grid grid-cols-1 sm:grid-cols-2 gap-5">
          {cards.map((card) => {
            const Icon = card.icon;
            return (
              <button
                key={card.title}
                onClick={card.onClick}
                disabled={card.disabled}
                className={`
                  group relative overflow-hidden rounded-2xl border border-border
                  bg-gradient-to-br ${card.gradient} backdrop-blur-xl
                  p-8 text-left transition-all duration-300
                  hover:scale-[1.02] hover:border-primary/40 hover:shadow-2xl hover:shadow-primary/10
                  disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100
                `}
              >
                <div className="flex flex-col gap-4">
                  <div className={`w-14 h-14 rounded-xl bg-background/40 backdrop-blur-sm flex items-center justify-center ${card.iconColor}`}>
                    <Icon className="h-7 w-7" />
                  </div>
                  <div>
                    <h2 className="text-2xl font-bold mb-1">{card.title}</h2>
                    <p className="text-sm text-muted-foreground">{card.description}</p>
                  </div>
                </div>
                <div className="absolute inset-0 bg-gradient-to-tr from-transparent via-transparent to-white/5 pointer-events-none" />
              </button>
            );
          })}
        </div>
      </div>
    </div>
  );
};

export default Recepcionista;
